import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';

export interface TimeProposal {
  id: string;
  sessionId: string;
  proposedBy: 'STUDENT' | 'TUTOR';
  proposedByUserId: string;
  proposedStartTime: string;
  duration: number;
  status: 'PENDING' | 'ACCEPTED' | 'SUPERSEDED';
  createdAt: string;
  respondedAt: string | null;
}

export interface Hold {
  id: string;
  sessionId: string;
  timeProposalId: string;
  tutorId: string;
  proposedStartTime: string;
  duration: number;
  expiryTime: string;
  status: 'ACTIVE' | 'EXPIRED' | 'ACCEPTED';
  createdAt: string;
}

export interface Session {
  id?: string; // String UUID for API operations
  sessionId: number;
  sessionRequestId: string;
  studentId: number;
  studentName: string;
  tutorId: number;
  tutorName: string;
  subject: string;
  scheduledStartTime: string | null;
  timeChangedFrom: string | null;
  duration: number;
  status: string;
  proposalAcceptedByStudent: boolean;
  proposalAcceptedByTutor: boolean;
  confirmedByStudent: boolean;
  confirmedByTutor: boolean;
  createdAt: string;
  updatedAt: string;
  notes?: string | null;
  meetingLink?: string | null;  // Deprecated - use meetingUrl
  fileAttachmentUrls?: string[] | null;
  activeProposal?: TimeProposal;
  activeHold?: Hold;
  // Recurring series fields (null for standalone sessions)
  seriesId?: string | null;
  occurrenceIndex?: number | null;
  occurrenceDate?: string | null;  // LocalDate as YYYY-MM-DD
  // Meeting fields (Google Meet integration)
  meetingUrl?: string | null;
  meetingProvider?: 'GOOGLE_MEET' | 'ZOOM' | 'TEAMS' | null;
  meetingCreatedAt?: string | null;
  // Cancel tracking fields (null if not cancelled)
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  cancelledByName?: string | null;
  cancelReason?: string | null;
  // Soft delete fields (null if not deleted)
  deletedAt?: string | null;
  deletedBy?: string | null;
}

export function useSessions(status?: string, enabled = true) {
  const { activeOrganization } = useAuth();
  const orgId = activeOrganization?.id ?? null;
  return useQuery<Session[]>({
    queryKey: ['sessions', status, orgId],
    queryFn: async () => {
      const params = status ? { status } : {};
      const response = await apiClient.get('/sessions/my-sessions', { params });
      return response.data;
    },
    enabled,
  });
}

export function useAdminSessions(filters?: { status?: string; studentId?: string; tutorId?: string; includeDeleted?: boolean }, enabled = true) {
  const { activeOrganization } = useAuth();
  const orgId = activeOrganization?.id ?? null;
  return useQuery<Session[]>({
    queryKey: ['admin-sessions', filters, orgId],
    queryFn: async () => {
      const response = await apiClient.get('/admin/sessions', { params: filters });
      return response.data;
    },
    enabled,
  });
}

// === Time Proposal Hooks (US3) ===

export interface TimeProposalActionResponse {
  sessionId: string;
  proposalId: string;
  holdId: string | null;
  status: string;
  acceptedByStudent?: boolean | null;
  acceptedByTutor?: boolean | null;
}

export interface ProposeTimePayload {
  proposedStartTime: string; // ISO string
  duration: number; // minutes
  holdExpiryHours?: number;
}

/**
 * Create or update a time proposal for a session
 */
export function useSessionProposeTime(sessionId: string | null) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationKey: ['sessions', sessionId, 'propose-time'],
    mutationFn: async (payload: ProposeTimePayload): Promise<TimeProposalActionResponse> => {
      if (!sessionId) throw new Error('sessionId is required to propose time');
      const response = await apiClient.post(`/sessions/${sessionId}/propose-time`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/**
 * Accept an existing time proposal
 */
export function useAcceptProposal(proposalId: string | null) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationKey: ['time-proposals', proposalId, 'accept'],
    mutationFn: async (): Promise<TimeProposalActionResponse> => {
      if (!proposalId) throw new Error('proposalId is required to accept');
      const response = await apiClient.post(`/time-proposals/${proposalId}/accept`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/**
 * Counter-propose with a new proposed time
 */
export function useCounterPropose(proposalId: string | null) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationKey: ['time-proposals', proposalId, 'counter-propose'],
    mutationFn: async (payload: ProposeTimePayload): Promise<TimeProposalActionResponse> => {
      if (!proposalId) throw new Error('proposalId is required to counter-propose');
      const response = await apiClient.post(`/time-proposals/${proposalId}/counter-propose`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

// === Session Feedback Hooks (Phase 1 - Recurring Sessions) ===

export interface SessionFeedbackRequest {
  studentAttended: boolean;
  rating?: number | null;                // 1-5, optional
  notes?: string | null;                 // required when attended
  topicsCovered?: string | null;         // required when attended
  homework?: string | null;              // optional
  studentEngagement?: number | null;     // 1-5, optional
  areasForImprovement?: string | null;   // optional
  progressSummary?: string | null;       // optional
}

export interface SessionFeedbackResponse {
  feedbackId: string;
  sessionId: string;
  sessionStatus: 'COMPLETED' | 'NO_SHOW';
  studentAttended: boolean;
  rating: number | null;
  notes: string | null;
  topicsCovered: string | null;
  homework: string | null;
  studentEngagement: number | null;
  areasForImprovement: string | null;
  progressSummary: string | null;
  submittedAt: string;
}

/**
 * Submit session feedback (tutor only)
 * Marks session as COMPLETED or NO_SHOW based on studentAttended
 */
export function useSubmitSessionFeedback(sessionId: string | null) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationKey: ['sessions', sessionId, 'feedback'],
    mutationFn: async (payload: SessionFeedbackRequest): Promise<SessionFeedbackResponse> => {
      if (!sessionId) throw new Error('sessionId is required to submit feedback');
      const response = await apiClient.post(`/sessions/${sessionId}/feedback`, payload);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate sessions to refresh the list with new status
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/**
 * Update existing session feedback (tutor only)
 * Note: studentAttended cannot be changed after initial submission
 */
export function useUpdateSessionFeedback(sessionId: string | null) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationKey: ['sessions', sessionId, 'feedback', 'update'],
    mutationFn: async (payload: SessionFeedbackRequest): Promise<SessionFeedbackResponse> => {
      if (!sessionId) throw new Error('sessionId is required to update feedback');
      const response = await apiClient.put(`/sessions/${sessionId}/feedback`, payload);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate feedback query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['sessions', sessionId, 'feedback'] });
    },
  });
}

/**
 * Get feedback for a session (if it exists)
 */
export function useSessionFeedback(sessionId: string | null) {
  return useQuery<SessionFeedbackResponse | null>({
    queryKey: ['sessions', sessionId, 'feedback'],
    queryFn: async () => {
      if (!sessionId) return null;
      try {
        const response = await apiClient.get(`/sessions/${sessionId}/feedback`);
        return response.data;
      } catch (error: any) {
        // 404 means no feedback yet - that's ok
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!sessionId,
  });
}

// === Session Cancel / Delete / Restore Hooks ===

/**
 * Cancel a session as a tutor or student.
 * POST /api/v1/sessions/{id}/cancel
 */
export function useCancelSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, reason }: { sessionId: string; reason?: string }) => {
      const body = reason ? { reason } : undefined;
      const response = await apiClient.post(`/sessions/${sessionId}/cancel`, body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-sessions'] });
    },
  });
}

/**
 * Cancel a session as an admin (bypasses time/status restrictions).
 * POST /api/v1/admin/sessions/{id}/cancel
 */
export function useAdminCancelSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, reason }: { sessionId: string; reason?: string }) => {
      const body = reason ? { reason } : undefined;
      const response = await apiClient.post(`/admin/sessions/${sessionId}/cancel`, body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/**
 * Soft-delete a session (admin only).
 * POST /api/v1/admin/sessions/{id}/delete
 */
export function useAdminDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, reason }: { sessionId: string; reason?: string }) => {
      const body = reason ? { reason } : undefined;
      const response = await apiClient.post(`/admin/sessions/${sessionId}/delete`, body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/**
 * Restore a soft-deleted session (admin only).
 * POST /api/v1/admin/sessions/{id}/restore
 */
export function useAdminRestoreSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId }: { sessionId: string }) => {
      const response = await apiClient.post(`/admin/sessions/${sessionId}/restore`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

